import { cacheExchange } from '@urql/exchange-graphcache';
import { relayPagination, simplePagination } from '@urql/exchange-graphcache/extras';

/**
 * Every nested mutation (`investbanq.clientRequest.delete`, `.restore`, etc.) hangs off this one
 * root `Mutation.investbanq` field, so that's the only place an `updates` resolver can hook in —
 * the shape here is whatever the specific mutation document that ran happened to select.
 */
interface InvestbanqMutationResult {
  clientRequest?: {
    delete?: { item?: { id: string } | null } | null;
    restore?: { item?: { id: string } | null } | null;
  } | null;
}

export const cache = cacheExchange({
  keys: {
    Client: (data) => data.id as string,
    Asset: (data) => data.id as string,
    Report: (data) => data.id as string,
    Task: (data) => data.id as string,
    CalendarEvent: (data) => data.id as string,
    Campaign: (data) => data.id as string,
    ClientRequest: (data) => data.id as string,
    BpmsWorkflow: (data) => data.id as string,
    BpmsStep: (data) => data.id as string,
    User: (data) => data.id as string,
    Role: (data) => data.id as string,
    Division: (data) => data.id as string,
    AuditLogEntry: (data) => data.id as string,
    Notification: (data) => data.id as string,
    Document: (data) => data.id as string,
    Portfolio: (data) => data.id as string,
    Invoice: (data) => data.id as string,
    PlatformCustomization: (data) => (data.tenantId as string | undefined) ?? null,
    TenantBranding: () => null,
    SocialLinks: () => null,
    Langs: () => null,
    TenantCompanyInformation: () => null,
    TenantAiDataPolicy: () => null,
    NotificationSettings: () => null,
    LanguageUsage: () => null,
    InterfaceSettings: () => null,
    AiPersonalization: () => null,
    EnpsPendingSurvey: () => null,
    // Connected accounts: a user's view of the catalog, read fresh every time (no ids).
    IntegrationConnectionView: () => null,
    IntegrationConnector: () => null,
    IntegrationConnectorLogo: () => null,
    IntegrationUrlPayload: () => null,
    IntegrationsMutationPayload: () => null,
  },
  resolvers: {
    Query: {
      clients: relayPagination(),
      assets: relayPagination(),
      deals: relayPagination(),
      invoices: relayPagination(),
      clientRequests: relayPagination(),
      tasks: relayPagination(),
      campaigns: relayPagination(),
      documents: relayPagination(),
      auditLogs: relayPagination(),
      bpmsWorkflows: relayPagination(),
      notifications: simplePagination({
        offsetArgument: 'offset',
        limitArgument: 'limit',
      }),
    },
    Portfolio: {
      totalAum: (parent) => {
        const holdings = (parent.holdings ?? []) as Array<{
          currentValue?: number;
        }>;
        return holdings.reduce((sum, h) => sum + (h.currentValue ?? 0), 0);
      },
    },
    Invoice: {
      isOverdue: (parent) => {
        const status = parent.status as string | undefined;
        const dueDate = parent.dueDate as string | undefined;
        return status === 'PENDING' && !!dueDate && new Date(dueDate) < new Date();
      },
    },
  },
  updates: {
    Mutation: {
      // Delete/restore change which list a ClientRequest belongs to (active vs. deleted), which
      // Graphcache can't infer on its own — it only sees an updated `deletedAt` field on an
      // entity that's already normalized in place. Invalidating the entity forces every active
      // query that included it (the requests table, this row's own drawer query) to be refetched,
      // instead of leaving a stale row until the page is manually reloaded.
      investbanq(result, _args, cache) {
        const clientRequest = (result as InvestbanqMutationResult | null | undefined)
          ?.clientRequest;
        const deletedId = clientRequest?.delete?.item?.id;
        const restoredId = clientRequest?.restore?.item?.id;
        if (deletedId) cache.invalidate({ __typename: 'ClientRequest', id: deletedId });
        if (restoredId) cache.invalidate({ __typename: 'ClientRequest', id: restoredId });
      },
    },
  },
});
